import { FunctionComponent, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { faCircleInfo, faPlus } from "@fortawesome/pro-regular-svg-icons";

import AppLogo from "@/assets/images/favicon.png";
import { useAppSelector } from "@/redux/hooks";
import { authSelector } from "@/pages/Public/redux/auth.slice";
import { ROUTE_CONFIG } from "@/routes/config";
import { Button } from "@/components/Button/Button";
import { ButtonSize } from "@/components/Button/types";
import { Icon } from "@/components/Icon/Icon";
import { usePrewarmPrimingDraftsMutation } from "@/pages/Private/redux/onboardingIcp/onboardingIcp.api";

import { skipsCheckout, useOnboardingVariant } from "@/utils/useOnboardingVariant";

import { OnboardingStepper } from "../components/OnboardingStepper";
import { TrackFilterPane } from "../components/track/TrackFilterPane";
import { OnboardingStepLoading } from "../components/OnboardingStepLoading";
import { TrackInterstitial } from "../components/track/TrackInterstitial";
import { TrackPeopleTable } from "../components/track/TrackPeopleTable";
import { TrackTableSkeleton } from "../components/track/TrackTableSkeleton";
import { TRACK_MAX, TRACK_MIN, useTrackPicker } from "../components/track/useTrackPicker";
import { usePrewarmPriming } from "../components/track/usePrewarmPriming";

// The ButtonSize.XS tokens plus `.input-styles`' border/hover — spelled out
// rather than reusing that class, which is `block w-full h-[48px]` and would
// stretch each toolbar button across half the card.
const toolbarButtonClass =
	"flex h-[32px] w-auto shrink-0 cursor-pointer items-center gap-2 rounded-[10px] border border-gray-200 bg-white px-3.5 text-xs font-semibold text-text-meta duration-200 hover:border-brand-light hover:text-brand-light disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-gray-200";

/**
 * Step 4b of the pre-checkout onboarding (PREC-3719, mockup v32 parity): the
 * suggested people to track, a "find different people" filter rail beside the
 * table, and find-more — the picks become a real tracker list, then the signal
 * interstitial hands over to checkout.
 */
export const OnboardingTrack: FunctionComponent = () => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`onboarding.track.${key}`);

	const navigate = useNavigate();
	const { user } = useAppSelector(authSelector);

	// Users who never pay themselves run the same wizard without its checkout
	// step (V2, PREC-3719) — the priming chat still follows this step.
	const skipCheckout = skipsCheckout(useOnboardingVariant());

	const [prewarmPrimingDrafts] = usePrewarmPrimingDraftsMutation();
	const picker = useTrackPicker();

	const [phase, setPhase] = useState<"pick" | "interstitial">("pick");

	usePrewarmPriming(prewarmPrimingDrafts);

	if (phase === "interstitial") {
		return <TrackInterstitial onContinue={() => navigate(ROUTE_CONFIG.ONBOARDING_PRIME)} />;
	}

	// The step opens filled or not at all. Its criteria are only settled once the
	// search has run — the relaxation ladder widens them when the ICP matches nobody,
	// so the rail's values are the rung that produced the table, not the seed. Shown
	// earlier, the rail stood empty beside a loading table, which reads as searching
	// with no criteria at all. See `OnboardingStepLoading`.
	if (picker.isLoading) {
		return (
			<OnboardingStepLoading
				subtitle={ts("preparingSubtitle")}
				title={ts("preparingTitle")}
				widthClass="w-[1280px]"
			/>
		);
	}

	const handleContinue = async () => {
		if (await picker.submit()) {
			setPhase("interstitial");
		}
	};

	return (
		<div className="w-full flex flex-col items-center justify-center">
			<div className="relative flex h-[950px] max-h-[92vh] w-[1280px] max-w-[100%] flex-col rounded-[20px] bg-white shadow-[0px_0px_80px_0px_rgba(171,178,217,0.35)]">
				<div className="px-8 pt-7">
					<div className="mb-3 flex justify-center">
						<img alt="logo" className="h-9 w-9" src={AppLogo} />
					</div>
					<div className="mb-1 text-center text-md font-bold">{ts("title")}</div>
					<div className="mb-4 text-center text-ssm text-text-meta">{ts("subtitle")}</div>

					<div className="mb-3 flex items-center gap-2">
						<button
							className={toolbarButtonClass}
							disabled={!picker.hasMore}
							type="button"
							onClick={picker.showMore}
						>
							<Icon icon={faPlus} />
							{ts("findMorePeople")}
						</button>
					</div>

					{/* Onboarding only ever adds people one at a time, and users take that
					    for the product's ceiling — so the bulk routes are named here rather
					    than discovered later (PREC-3904). */}
					<p className="mb-3 flex items-start gap-2 rounded-lg bg-blue-action-200 px-3 py-2 text-xs leading-relaxed text-text">
						<Icon className="mt-0.5 h-3 w-3 shrink-0 text-brand-light" icon={faCircleInfo} />
						<span>
							<span className="font-bold">{ts("bulkNoteTitle")}</span> {ts("bulkNote")}
						</span>
					</p>

					{/* "We widened the search" only reads right above rows it produced. */}
					{picker.relaxation && picker.visiblePeople.length > 0 && !picker.isSearching && (
						<div className="mb-3 rounded-lg bg-status-warning-light px-3 py-2 text-xs text-text-meta">
							{ts(picker.relaxation === "seniority" ? "relaxedToSeniority" : "relaxedToBroad")}
						</div>
					)}
				</div>

				<div className="flex min-h-0 flex-1 items-stretch gap-4 px-8 pb-4">
					{/* The criteria behind the table are always on show: they are what the
					    user changes when the suggestions are not who they meant, and a
					    table full of people is no reason to hide them. The step waits for
					    them (above), so the rail mounts once, already carrying the criteria
					    the table was built from. */}
					<TrackFilterPane
						initialValues={picker.initialFilters}
						isSearching={picker.isSearching}
						onSearch={picker.search}
					/>

					{picker.isSearching ? (
						<div className="min-w-0 flex-1">
							<TrackTableSkeleton />
						</div>
					) : (
						<TrackPeopleTable
							hasLoadFailed={picker.loadFailed}
							manualUrls={picker.manualUrls}
							people={picker.visiblePeople}
							selectedIds={picker.selectedIds}
							onAddPerson={picker.addPerson}
							onManualUrlsChange={picker.setManualUrls}
							onRemove={picker.remove}
							onRetryLoad={picker.retryLoad}
							onToggle={picker.toggle}
						/>
					)}
				</div>

				<div className="flex items-center justify-between border-t border-gray-200 px-6 py-4">
					<div className="flex items-center gap-4">
						<button
							className="cursor-pointer text-xs font-semibold text-text-meta hover:text-brand-light"
							type="button"
							onClick={() => navigate(ROUTE_CONFIG.ONBOARDING_LISTS)}
						>
							‹ {ts("back")}
						</button>
						<span className="text-xs font-semibold text-text-meta">
							{t("onboarding.track.selectedCount", {
								count: picker.totalSelected,
								max: TRACK_MAX,
							})}
						</span>
						{!picker.isSearching && picker.totalSelected < TRACK_MIN && (
							<span className="text-xs font-semibold text-status-error">
								{t("onboarding.track.minHint", { min: TRACK_MIN })}
							</span>
						)}
					</div>
					<OnboardingStepper step={3} total={skipCheckout ? 3 : 4} />
					<Button
						disabled={picker.totalSelected < TRACK_MIN || picker.isSubmitting || picker.isSearching}
						isLoading={picker.isSubmitting || picker.isSearching}
						size={ButtonSize.ML}
						title={ts("continue")}
						onClick={handleContinue}
					/>
				</div>

				{picker.toast && (
					<div className="absolute bottom-20 left-1/2 -translate-x-1/2 rounded-lg bg-gray-900 px-4 py-2 text-xs text-white">
						{picker.toast}
					</div>
				)}
			</div>
		</div>
	);
};
